import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { produce } from 'immer';
import { filterArrayEmpty } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { isEqual, without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK, SET_GENERIC } from 'store/app/actions';
import { selectOrganisationFieldSettings } from 'store/remote/selectors';
import { FieldSettingsTypes } from 'store/remote/types';
import { FieldTypeKey } from 'types/types';
import { useEsc } from 'hooks/useEsc';
import { useConfirmUnsaved } from 'hooks/useConfirm';

type Criterion = {
  fieldId: string;
  value: string[];
  operator: 'includes';
};

export const FieldSettingsFieldCriteriaModal: React.FC<
  ModalScreenProps<ModalScreens.FieldSettingsFieldCriteria>
> = ({ navigation, route }) => {
  const { fieldId, groupId, selectionKey, criteria } = route.params;
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const fieldSettings = useSelector(selectOrganisationFieldSettings);
  const group = fieldSettings.groups[groupId]!;

  const [newCriteria, setNewCriteria] = React.useState<
    Record<string, Criterion>
  >(
    criteria.reduce(
      (acc, criterion) => ({
        ...acc,
        [criterion.fieldId]: criterion,
      }),
      {},
    ),
  );

  const fields = React.useMemo(
    () =>
      group.fieldIds
        .map((fldId) => {
          // Don't include the current field
          if (fldId === fieldId) {
            return null;
          }
          const fld = fieldSettings.fields[fldId];
          if (!fld || !fld.options.length) {
            return null;
          }
          const type = FieldSettingsTypes[fld.type];
          // Only allow options and checkboxes
          if (
            type.id !== FieldTypeKey.options &&
            type.id !== FieldTypeKey.checkbox
          ) {
            return null;
          }
          return { ...fld, type };
        })
        .filter(filterArrayEmpty),
    [fieldId, fieldSettings.fields, group.fieldIds],
  );

  const fieldParentFields = fields.reduce<Record<string, string[]>>(
    (acc, fld) =>
      fld.criteria.reduce<Record<string, string[]>>(
        (acc1, criterion) => ({
          ...acc1,
          [criterion.fieldId]: [...(acc1[criterion.fieldId] || []), fld.id],
        }),
        acc,
      ),
    {},
  );

  const addParentCriteria = (
    childFieldId: string,
    parentFieldIds: string[] = [],
  ) => {
    let copiedParentFieldIds = [...parentFieldIds];
    if (fieldParentFields[childFieldId]) {
      fieldParentFields[childFieldId]!.forEach((parentFieldId) => {
        copiedParentFieldIds.push(parentFieldId);
        copiedParentFieldIds = addParentCriteria(
          parentFieldId,
          copiedParentFieldIds,
        );
      });
    }
    return copiedParentFieldIds;
  };
  const childFieldIds = addParentCriteria(fieldId);

  const handlePressOption = (fldId: string, optionId: string) => {
    setNewCriteria((eOV) =>
      produce(eOV, (draft) => {
        if (!draft[fldId]) {
          draft[fldId] = {
            fieldId: fldId,
            operator: 'includes',
            value: [optionId],
          };
        } else if (!draft[fldId]!.value.includes(optionId)) {
          draft[fldId]!.value.push(optionId);
        } else {
          draft[fldId]!.value = without(draft[fldId]!.value, optionId);
          if (!draft[fldId]!.value.length) {
            delete draft[fldId];
          }
        }
        return draft;
      }),
    );
  };

  const isDirtyVar = !isEqual(criteria, Object.values(newCriteria));

  const handleSave = () => {
    dispatch(
      SET_GENERIC({
        key: selectionKey,
        value: Object.values(newCriteria),
        temp: true,
      }),
    );
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleClose = () => {
    if (isDirtyVar) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Show Field If..."
          onPressCancelButton={handleClose}
          onPressSaveButton={isDirtyVar ? handleSave : null}
          saveButtonText="Done"
        />
      }
    >
      <CardGeneric margin={false}>
        <Txt>
          This field can be hidden and only appear when other fields are
          answered. Choose which values make this field appear; if nothing is
          chosen the field will always be visible
        </Txt>
      </CardGeneric>

      <SeparatorHorizontal marginVertical={theme.paddingVertical} />

      {fields.length ? (
        fields.map((fld) => (
          <Card label={fld.name} key={fld.id}>
            {childFieldIds.includes(fld.id) ? (
              <CardRow value="Cannot be included as it relies on the field you're currently editing for visibility" />
            ) : (
              !!fld.options.length &&
              fld.options.map((option) => (
                <CardRow
                  label={option.name}
                  key={option.id}
                  onPress={() => handlePressOption(fld.id, option.id)}
                  checkedValue={(newCriteria[fld.id]?.value || []).includes(
                    option.id,
                  )}
                />
              ))
            )}
          </Card>
        ))
      ) : (
        <CardGeneric>
          <Txt>There are no fields available to choose from.</Txt>
        </CardGeneric>
      )}
    </PageScrollView>
  );
};
